import { TGetContactDetailResDto } from "@/api/services/admin/contact/dtos/get-contact-detail.dto";
import { TGetContactListReqDto, TGetContactListResDto } from "@/api/services/admin/contact/dtos/get-contact-list.dto";
import { TUpdateContactIsResolvedResDto } from "@/api/services/admin/contact/dtos/update-contact.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { useIsPageParamValid } from "@/hooks/useSanitizedPageParam";
import { TApiSortsReq } from "@/types/api";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useEffect } from "react";

export const fetchContactListApi = async (req: TGetContactListReqDto): Promise<TGetContactListResDto> => {
  const url = createUrlWithParams("/contacts", req);
  const { data } = await clientApi.get<TGetContactListResDto>(url);
  return data;
};

export const useContactList = (isResolved: boolean | boolean[], sorts?: TApiSortsReq[]) => {
  const pagReq = usePagination();
  const isValidPage = useIsPageParamValid();
  const contactReq = { ...pagReq, isResolved, sorts };
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST, contactReq],
    queryFn: () => fetchContactListApi(contactReq),
    enabled: Boolean(isResolved) && isValidPage,
  });

  useEffect(() => {
    if (result.isSuccess) {
      const isUnresolvedList = Array.isArray(isResolved) ? isResolved.includes(false) : isResolved === false;

      if (isUnresolvedList) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT.UNREAD_COUNT] });
      }
    }
  }, [result.isSuccess, isResolved, queryClient]);

  return result;
};

export const fetchContactDetailApi = async (contactId: string): Promise<TGetContactDetailResDto> => {
  const rs = await clientApi.get<TGetContactDetailResDto>(`/contacts/${contactId}`);
  return rs.data;
};

export const useContact = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [QUERY_KEYS.CONTACT.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchContactDetailApi(id) : undefined),
    enabled: Boolean(id && isNumber(id)),
  });

  useEffect(() => {
    if (result.isSuccess) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT.UNREAD_COUNT] });
    }
  }, [result.isSuccess, queryClient]);

  return result;
};

export const updateContactIsResolved = async (contactId: number): Promise<TUpdateContactIsResolvedResDto> => {
  const rs = await clientApi.put<TUpdateContactIsResolvedResDto>(`/contacts/${contactId}/is-resolved`);
  return rs.data;
};

export const fetchUnreadContactCountApi = async (): Promise<number> => {
  const { data } = await clientApi.get<{ data: number }>("/contacts/unread-count");
  return data.data;
};

export const useUnreadContactCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.CONTACT.UNREAD_COUNT],
    queryFn: () => fetchUnreadContactCountApi(),
  });
};
